import React, { useEffect, useMemo, useState } from "react"
import "./HeatmapDashboard.css"
import redirectURL from "../../redirectURL" // keep your path as per project

export default function HeatmapDashboard(props) {
  var globalFilters = props.globalFilters || null
  var activeSubTab = props.activeSubTab || "warehouse" // "warehouse" | "hub" | "gurgaon"

  var [loading, setLoading] = useState(true)
  var [lastRefreshTime, setLastRefreshTime] = useState(null)

  // ✅ master dropdown data from API
  var [whHubMaster, setWhHubMaster] = useState([]) // [{ warehouse: "RPDC-GUJ", hub: ["PUNE", ...] }, ...]

  // Heatmap filters (controlled)
  var [selectedWarehouse, setSelectedWarehouse] = useState("all")
  var [selectedHubLocation, setSelectedHubLocation] = useState("all")
  var [timePeriod, setTimePeriod] = useState("weekly") // weekly | monthly | yearly
  var [fromDate, setFromDate] = useState("2025-12-01")
  var [toDate, setToDate] = useState("2026-01-31")

  // API grid rows (flat)
  var [apiTrend, setApiTrend] = useState([]) // warehouse tab grid
  var [apiHubTrend, setApiHubTrend] = useState([]) // hub tab grid
  var [apiGurgaonTrend, setApiGurgaonTrend] = useState([]) // gurgaon tab grid

  // Delay ranges (WAREHOUSE TAB uses 0-24, 24-36, 36-48, >48)
  var delayRanges = useMemo(function () {
    return ["0-24hrs", "24-36hrs", "36-48hrs", ">48hrs"]
  }, [])

  // Delay ranges (GURGAON TAB uses 0-48, 48-90, 90-120, >120)
  var gurgaonDelayRanges = useMemo(function () {
    return ["0-48hrs", "48-90hrs", "90-120hrs", ">120hrs"]
  }, [])

  function formatRefreshTime(date) {
    if (!date) return "Never"
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }


  function clearFilters() {
    setSelectedWarehouse("all")
    setSelectedHubLocation("all")
    setTimePeriod("weekly")
    setFromDate("2025-12-01")
    setToDate("2026-01-31")
  }

  // ----------------------------
  // ✅ Tab mapping for master API
  // Default MUST be "all_warehouses"
  // ----------------------------
  function getMasterTab(activeSubTab) {
    if (activeSubTab === "gurgaon") return "gurgaon"
    if (activeSubTab === "hub") return "hub"
    return "all_warehouses" // default for warehouse
  }

  // ----------------------------
  // API Calls (NODE wrappers)
  // ----------------------------
  async function fetchHeatmapTrendAllFromApi(params) {
    var url = "/msildashboard/fetchTransitDelayHeatmap"
    var response = await redirectURL.post(url, params)
    var responseData =
      response && response.data && response.data.status === "Success" ? response.data : null

    if (!responseData) {
      // eslint-disable-next-line no-console
      console.warn("API failed:", response && response.data ? response.data : response)
      return { trend_data: [] }
    }

    return responseData
  }

  async function fetchHeatmapTrendGurgaonFromApi(params) {
    var url = "/msildashboard/fetchTransitDelayHeatmapGurgaon"
    var response = await redirectURL.post(url, params)
    var responseData =
      response && response.data && response.data.status === "Success" ? response.data : null

    if (!responseData) {
      // eslint-disable-next-line no-console
      console.warn("Gurgaon API failed:", response && response.data ? response.data : response)
      return { trend_data: [] }
    }

    return responseData
  }

  // ✅ Fetch warehouses + hubs master (tab-based)
  async function fetchWarehousesAndHubsFromApi(activeSubTab) {
    var url = "/msildashboard/fetchWarehousesAndHubs"

    var payload = { tab: getMasterTab(activeSubTab) } // ✅ tab depends on switch
    var response = await redirectURL.post(url, payload)

    // backend returns: { status:"Success", data:[...] }
    var data = response && response.data ? response.data.data : null

    if (!Array.isArray(data)) {
      // eslint-disable-next-line no-console
      console.warn(
        "Warehouses/Hubs API failed or unexpected:",
        response && response.data ? response.data : response
      )
      return []
    }

    return data
  }

  // ----------------------------
  // ✅ EFFECT: fetch warehouses + hubs master list
  // - Runs on tab switch (warehouse/hub/gurgaon)
  // - Default is all_warehouses
  // - Resets filters to "all" on switch (so dropdowns don't get stuck)
  // ----------------------------
  useEffect(
    function () {
      var isMounted = true

      async function loadMaster() {
        try {
          var master = await fetchWarehousesAndHubsFromApi(activeSubTab)
          if (!isMounted) return

          // normalize a bit (Node 12 friendly)
          var cleaned = []
          for (var i = 0; i < master.length; i++) {
            var item = master[i]
            if (!item) continue
            var w = item.warehouse
            var h = item.hub
            if (!w) continue
            if (!Array.isArray(h)) h = []
            cleaned.push({ warehouse: String(w), hub: h.map(String) })
          }

          setWhHubMaster(cleaned)

          // ✅ Reset selection to defaults on tab switch
          // so Gurgaon view starts with "All"
          setSelectedWarehouse("all")
          setSelectedHubLocation("all")
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
          if (!isMounted) return
          setWhHubMaster([])
          setSelectedWarehouse("all")
          setSelectedHubLocation("all")
        }
      }

      loadMaster()

      return function () {
        isMounted = false
      }
    },
    [activeSubTab]
  )

  // ✅ Warehouses dropdown from API master
  var warehouses = useMemo(
    function () {
      var set = new Set()
      for (var i = 0; i < whHubMaster.length; i++) {
        var w = whHubMaster[i] && whHubMaster[i].warehouse
        if (w && w !== "all") set.add(w)
      }
      var arr = Array.from(set)

      // fallback (optional) – keep empty if API has no data
      return arr.length ? arr : []
    },
    [whHubMaster]
  )

  // ✅ Hub locations dropdown from API master (depends on selectedWarehouse)
  var hubLocations = useMemo(
    function () {
      var set = new Set()

      if (selectedWarehouse === "all") {
        // union of all hubs
        for (var i = 0; i < whHubMaster.length; i++) {
          var hubs = whHubMaster[i] && whHubMaster[i].hub
          if (!Array.isArray(hubs)) continue
          for (var j = 0; j < hubs.length; j++) {
            if (hubs[j]) set.add(hubs[j])
          }
        }
      } else {
        // hubs for selected warehouse
        for (var k = 0; k < whHubMaster.length; k++) {
          var row = whHubMaster[k]
          if (row && row.warehouse === selectedWarehouse && Array.isArray(row.hub)) {
            for (var m = 0; m < row.hub.length; m++) {
              if (row.hub[m]) set.add(row.hub[m])
            }
            break
          }
        }
      }

      return Array.from(set).sort(function (a, b) {
        return String(a).localeCompare(String(b))
      })
    },
    [whHubMaster, selectedWarehouse]
  )

  // ✅ If warehouse changes and current hub is not available anymore, reset hub to "all"
  useEffect(
    function () {
      if (selectedHubLocation === "all") return
      var ok = false
      for (var i = 0; i < hubLocations.length; i++) {
        if (hubLocations[i] === selectedHubLocation) {
          ok = true
          break
        }
      }
      if (!ok) setSelectedHubLocation("all")
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedWarehouse, whHubMaster, hubLocations]
  )

  // ----------------------------
  // EFFECT: auto refetch on filter change (NO FLICKER)
  // ----------------------------
  useEffect(
    function () {
      var isMounted = true
      var requestId = Date.now()

      async function load() {
        try {
          setLoading(true)

          var payload = {
            by: timePeriod, // weekly | monthly | yearly
            hub: selectedHubLocation === "all" ? "all" : selectedHubLocation,
            warehouse: selectedWarehouse,
            from_date: fromDate,
            to_date: toDate,
          }

          var apiResp = null
          if (activeSubTab === "gurgaon") {
            apiResp = await fetchHeatmapTrendGurgaonFromApi(payload)
          } else {
            apiResp = await fetchHeatmapTrendAllFromApi(payload)
          }

          if (!isMounted) return
          if (load.__lastRequestId && load.__lastRequestId !== requestId) return

          var flatRows = normalizeTrendResponseToGridRows(apiResp, payload, activeSubTab)

          if (activeSubTab === "hub") {
            setApiHubTrend(flatRows)
          } else if (activeSubTab === "gurgaon") {
            setApiGurgaonTrend(flatRows)
          } else {
            setApiTrend(flatRows)
          }

          setLastRefreshTime(new Date())
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
          if (!isMounted) return
          setLastRefreshTime(new Date())
        } finally {
          if (isMounted) setLoading(false)
        }
      }

      load.__lastRequestId = requestId
      load()

      return function () {
        isMounted = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timePeriod, selectedWarehouse, selectedHubLocation, fromDate, toDate, activeSubTab]
  )

  // ----------------------------
  // Build grid columns from API x_axis
  // ----------------------------
  var currentGrid = useMemo(function () {
    if (activeSubTab === "hub") return apiHubTrend
    if (activeSubTab === "gurgaon") return apiGurgaonTrend
    return apiTrend
  }, [apiTrend, apiHubTrend, apiGurgaonTrend, activeSubTab])

  var periods = useMemo(function () {
    var set = new Set()
    currentGrid.forEach(function (r) {
      if (r && r.period) set.add(r.period)
    })
    return Array.from(set)
  }, [currentGrid])

  function sortPeriods(list) {
    return list.slice().sort(function (a, b) {
      // Week N sort
      var wa = extractNumber(a)
      var wb = extractNumber(b)
      if (wa !== null && wb !== null && String(a).toLowerCase().indexOf("week") !== -1) return wa - wb

      // Year sort
      var ya = parseInt(a, 10)
      var yb = parseInt(b, 10)
      if (!isNaN(ya) && !isNaN(yb)) return ya - yb

      // fallback alphabetical (months)
      return String(a).localeCompare(String(b))
    })
  }

  var sortedPeriods = useMemo(
    function () {
      return sortPeriods(periods)
    },
    [periods, timePeriod]
  )

  // Determine which delay range list to show
  var activeRanges = useMemo(function () {
    if (activeSubTab === "gurgaon") return gurgaonDelayRanges
    return delayRanges
  }, [activeSubTab, delayRanges, gurgaonDelayRanges])

  // Intensity scaling based on max grCount in current grid
  var maxCount = useMemo(function () {
    var m = 0
    currentGrid.forEach(function (r) {
      m = Math.max(m, r.grCount || 0)
    })
    return m
  }, [currentGrid])

  // ----------------------------
  // UI
  // ----------------------------
  return (
    <div className="hmPage">
      <div className="hmSection">
        <div className="hmHeader">
          <div className={activeSubTab === "gurgaon" ? "hmTitleAccent hmAccentOrange" : "hmTitleAccent hmAccentBlue"}>
            <h2 className="hmTitle">Hub and Spoke Delivery Time Heatmap</h2>
          </div>

          <div className="hmRefresh">
            Last refreshed: {formatRefreshTime(lastRefreshTime)}
            {loading ? <span style={{ marginLeft: 10 }}>(Loading...)</span> : null}
          </div>
        </div>

        {/* FILTER CARD */}
        <div className="hmCard">
          <div className="hmCardHeader">
            <div className="hmCardTitle">Heatmap Filters</div>
          </div>

          <div className="hmCardBody hmCardBodyCompact">
            <div className="hmFilterGrid">
              <div className="hmField">
                <label className="hmLabel" htmlFor="from-date">From Date</label>
                <input
                  id="from-date"
                  className="hmInput"
                  type="date"
                  value={fromDate}
                  onChange={function (e) { setFromDate(e.target.value) }}
                />
              </div>

              <div className="hmField">
                <label className="hmLabel" htmlFor="to-date">To Date</label>
                <input
                  id="to-date"
                  className="hmInput"
                  type="date"
                  value={toDate}
                  onChange={function (e) { setToDate(e.target.value) }}
                />
              </div>

              <div className="hmField">
                <label className="hmLabel" htmlFor="warehouse">Warehouse</label>
                <div className="hmSelectWrap">
                  <select
                    id="warehouse"
                    className="hmSelect"
                    value={selectedWarehouse}
                    onChange={function (e) { setSelectedWarehouse(e.target.value) }}
                  >
                    <option value="all">All</option>
                    {warehouses.map(function (w) {
                      return (
                        <option key={w} value={w}>{w}</option>
                      )
                    })}
                  </select>
                  <span className="hmCaret">▾</span>
                </div>
              </div>

              <div className="hmField">
                <label className="hmLabel" htmlFor="hub-location">Hub</label>
                <div className="hmSelectWrap">
                  <select
                    id="hub-location"
                    className="hmSelect"
                    value={selectedHubLocation}
                    onChange={function (e) { setSelectedHubLocation(e.target.value) }}
                    disabled={hubLocations.length === 0}
                  >
                    <option value="all">All</option>
                    {hubLocations.map(function (loc) {
                      return <option key={loc} value={loc}>{loc}</option>
                    })}
                  </select>
                  <span className="hmCaret">▾</span>
                </div>
              </div>

              <div className="hmField">
                <label className="hmLabel" htmlFor="time-period">Time Period</label>
                <div className="hmSelectWrap">
                  <select
                    id="time-period"
                    className="hmSelect"
                    value={timePeriod}
                    onChange={function (e) { setTimePeriod(e.target.value) }}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <span className="hmCaret">▾</span>
                </div>
              </div>

              <div className="hmField hmFieldBtn">
                <button className="hmBtn hmBtnOutline" type="button" onClick={clearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* HEATMAP CARD */}
        <div className="hmCard">
          <div className="hmCardHeader">
            <div className="hmCardTitle">
              {activeSubTab === "hub"
                ? "Hub Transit Delay Heatmap"
                : activeSubTab === "gurgaon"
                  ? "Gurgaon Transit Delay Heatmap"
                  : "Warehouse Transit Delay Heatmap"}
            </div>

            <div className="hmSubText">
              Showing GR count distribution across time periods and delay ranges
              {selectedWarehouse !== "all" ? " for " + selectedWarehouse : ""}
              {selectedHubLocation !== "all" ? " | Hub: " + selectedHubLocation : ""}
            </div>
          </div>

          <div className="hmCardBody">
  {loading ? (
    <div className="hmHeatmapLoader">
      <div className="hmHeatmapSpinner"></div>
      <div className="hmHeatmapLoaderText">Loading heatmap...</div>
    </div>
  ) : (
    <>
      <div className="hmLegendRow">
        <div className="hmLegendTitle">GR Count Intensity:</div>
        <div className="hmLegendScale">
          <span className="hmLegendSmall">Low</span>
          <div className="hmLegendBlocks">
            {activeRanges.map(function (rangeLabel, idx) {
              return (
                <div
                  key={idx}
                  className="hmLegendBlock"
                  title={prettyRange(rangeLabel)}
                  style={{
                    backgroundColor: getRowColor(rangeLabel, activeSubTab),
                  }}
                />
              )
            })}
          </div>
          <span className="hmLegendSmall">High</span>
        </div>
      </div>

      <div className="hmScrollX">
        <div className="hmMinWidth">
          <div className="hmGridHeader">
            <div className="hmHeaderCell hmHeaderLeft">
              Delay Range / {timePeriod === "weekly" ? "Weekly" : timePeriod === "monthly" ? "Monthly" : "Yearly"} Period
            </div>
            {sortedPeriods.map(function (p) {
              return (
                <div key={p} className="hmHeaderCell hmHeaderRight">
                  {p}
                </div>
              )
            })}
          </div>

          {activeRanges.map(function (rangeLabel) {
            return (
              <div key={rangeLabel} className="hmGridRow">
                <div className="hmRowLabel">{prettyRange(rangeLabel)}</div>

                {sortedPeriods.map(function (p) {
                  var row = findCell(currentGrid, p, rangeLabel)

                  var count = row ? row.grCount : 0
                  var pct = row ? row.percentage : 0
                  var bg = getRowColor(rangeLabel, activeSubTab)

                  return (
                    <div
                      key={rangeLabel + "-" + p}
                      className="hmCell"
                      style={{ backgroundColor: bg }}
                      title={prettyRange(rangeLabel) + " - " + p + ": " + count + " (" + formatPct(pct) + ") GRs"}
                    >
                      {count > 0 ? formatCellValue(count, pct) : ""}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )}
</div>
        </div>
      </div>
    </div>
  )
}

/* ---------------- Helpers (pure) ---------------- */

function normalizeTrendResponseToGridRows(apiResp, payload, activeSubTab) {
  // Node returns: { trend_data: [...] }
  var raw = apiResp && apiResp.trend_data ? apiResp.trend_data : []
  if (!Array.isArray(raw)) raw = []

  var out = []

  for (var i = 0; i < raw.length; i++) {
    var periodObj = raw[i]
    if (!periodObj) continue

    var period = periodObj.x_axis
    var arr = periodObj.data
    if (!period || !Array.isArray(arr)) continue

    for (var j = 0; j < arr.length; j++) {
      var item = arr[j]
      if (!item) continue

      var range = item.range
      var count = typeof item.count === "number" ? item.count : parseFloat(item.count || 0)
      var pct = typeof item.percentage === "number" ? item.percentage : parseFloat(item.percentage || 0)

      if (!range) continue

      out.push({
        period: period,
        delayRange: normalizeRange(range, activeSubTab),
        grCount: isNaN(count) ? 0 : count,
        percentage: isNaN(pct) ? 0 : pct,
        warehouse: payload && payload.warehouse ? payload.warehouse : "all",
        hub: payload && payload.hub ? payload.hub : "all",
      })
    }
  }

  return out
}

function normalizeRange(r, activeSubTab) {
  var s = String(r).trim()
  s = s.replace(/\s+/g, "")
  s = s.replace(/HRS$/i, "hrs")
  s = s.replace(/HR$/i, "hrs")
  return s
}

function findCell(rows, period, range) {
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i]
    if (r && r.period === period && r.delayRange === range) return r
  }
  return null
}

function extractNumber(label) {
  if (!label) return null
  var m = String(label).match(/(\d+)/)
  return m ? parseInt(m[1], 10) : null
}

function prettyRange(rangeLabel) {
  var s = String(rangeLabel)
  s = s.replace("hrs", " hrs")
  s = s.replace("> ", ">")
  return s
}

function formatPct(p) {
  var n = typeof p === "number" ? p : parseFloat(p || 0)
  if (isNaN(n)) n = 0
  return n.toFixed(2) + "%"
}

function formatCellValue(count, pct) {
  // "3100 (58.49%)"
  return String(count) + " (" + formatPct(pct) + ")"
}
function getRowColor(rangeLabel, activeSubTab) {
  var r = String(rangeLabel || "").replace(/\s+/g, "").toLowerCase();

  // Gurgaon ranges
  if (activeSubTab === "gurgaon") {
    if (r === "0-48hrs") return "#dff3e4";   // soft green
    if (r === "48-90hrs") return "#eef7d6";  // light green-yellow
    if (r === "90-120hrs") return "#fde7c7"; // soft orange
    if (r === ">120hrs") return "#f8d7da";   // soft red
    return "#f3f4f6";
  }

  // Warehouse / Hub ranges
  if (r === "0-24hrs") return "#dff3e4";   // green
  if (r === "24-36hrs") return "#eef7d6";  // light green
  if (r === "36-48hrs") return "#fde7c7";  // orange shade
  if (r === ">48hrs") return "#f8d7da";    // red shade

  return "#f3f4f6";
}